import React, { useEffect, useRef, useState } from 'react';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AutoFixOffIcon from '@mui/icons-material/AutoFixOff';
import RepeatIcon from '@mui/icons-material/Repeat';
import { IconButton, InputAdornment } from '@mui/material';

import {
  formatLengthToArchitecturalString,
  parseImperialLengthToInches,
  PropertyTextInput,
} from '@paragon/client-tools';
import { isInput, notNullish } from '@paragon/utilities';

import { TrussEnvelopeSnappingSettings } from '../../../../../library/layout-geometry/truss-envelopes/types';
import { layoutSlice } from '../../../../../store/slices/layoutSlice';
import { useAppDispatch } from '../../../../hooks';
import { SnappingSettingsTooltip } from './SnappingSettingsTooltip';

export function TrussEnvelopeSnappingDistanceField(props: {
  configuredSettings: TrussEnvelopeSnappingSettings;
  actualSettings: TrussEnvelopeSnappingSettings | null;
  disabled?: boolean;
}) {
  const dispatch = useAppDispatch();
  const [didDistanceParsingError, setDidDistanceParsingError] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (isInput(event.target as HTMLElement) || inputRef.current == null) {
        return;
      }

      if (event.altKey && event.key === 's') {
        event.preventDefault();

        inputRef.current.select();
        inputRef.current.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => void document.removeEventListener('keydown', onKeyDown);
  }, [inputRef]);

  const currentSettings = props.actualSettings ?? props.configuredSettings;

  const [isEscapingAutomaticMode, setIsEscapingAutomaticMode] = useState(false);

  function startEscapeFromAutomaticMode() {
    setIsEscapingAutomaticMode(true);
    setTimeout(() => {
      inputRef.current!.select();
    });
  }

  function finishEscapingAutomaticMode() {
    setIsEscapingAutomaticMode(false);
    dispatch(layoutSlice.actions.automaticTrussEnvelopeSnappingEnabled());
  }

  return (
    <PropertyTextInput
      sx={{
        maxWidth: '25ch',

        // an outlined text input now has a fieldset that blocks the entire input; MUI disables pointer events for it,
        //  but that rule is unfortunately overridden by a higher specificity rule from `FloatingOverSceneColumn`.
        '& fieldset': { pointerEvents: 'none' },
      }}
      property="Snapping Distance(s)"
      error={didDistanceParsingError}
      value={
        isEscapingAutomaticMode
          ? ''
          : currentSettings.distanceSettings === 'automatic'
            ? 'automatic'
            : currentSettings.distanceSettings.distances.map(formatLengthToArchitecturalString).join(', ')
      }
      onFocus={(event) => {
        if (currentSettings.distanceSettings === 'automatic') {
          startEscapeFromAutomaticMode();
        } else {
          event.target.select();
        }
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SnappingSettingsTooltip title="Automatic mode—snapping distance varies depending on base line">
              <IconButton
                color={
                  !isEscapingAutomaticMode && props.configuredSettings.distanceSettings === 'automatic'
                    ? 'primary'
                    : undefined
                }
                onClick={() => {
                  if (props.configuredSettings.distanceSettings === 'automatic') {
                    startEscapeFromAutomaticMode();
                  } else {
                    finishEscapingAutomaticMode();
                  }
                }}
              >
                {!isEscapingAutomaticMode && props.configuredSettings.distanceSettings === 'automatic' ? (
                  <AutoFixHighIcon />
                ) : (
                  <AutoFixOffIcon />
                )}
              </IconButton>
            </SnappingSettingsTooltip>
          </InputAdornment>
        ),

        endAdornment: (
          <InputAdornment position="end">
            <SnappingSettingsTooltip title="Extended mode—include all increments of distance(s)">
              <IconButton
                color={
                  currentSettings.distanceSettings !== 'automatic' && currentSettings.distanceSettings.extended
                    ? 'primary'
                    : undefined
                }
                disabled={props.configuredSettings.distanceSettings === 'automatic' && !isEscapingAutomaticMode}
                onClick={() => {
                  setIsEscapingAutomaticMode(false);
                  dispatch(layoutSlice.actions.trussEnvelopeSnappingExtensionToggled());
                }}
              >
                <RepeatIcon />
              </IconButton>
            </SnappingSettingsTooltip>
          </InputAdornment>
        ),
      }}
      disabled={props.disabled}
      inputRef={inputRef}
      submit={(valueString) => {
        setDidDistanceParsingError(false);

        if (valueString.trim().length === 0) {
          finishEscapingAutomaticMode();
          return;
        }

        let values: number[];

        try {
          const parsedValues = valueString.split(',').map(parseImperialLengthToInches);

          if (parsedValues.some((value) => value == null)) {
            setDidDistanceParsingError(true);
            return;
          }

          values = parsedValues.filter(notNullish);
        } catch {
          setDidDistanceParsingError(true);
          return;
        }

        setIsEscapingAutomaticMode(false);
        dispatch(layoutSlice.actions.trussEnvelopeSnappingDistancesSet(values));
      }}
    />
  );
}
